// frontend/src/hooks/useEda.ts

import { useState, useEffect, useCallback } from 'react';
import { EdaService } from '../services/eda.service';
import { useAuth } from '../contexts/AuthContext';

export const useEda = () => {
  const { selectedTraceId, selectTrace } = useAuth();

  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState<any | null>(null);
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listSnapshots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await EdaService.listSnapshots();
      setSnapshots(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao listar snapshots');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSnapshot = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await EdaService.getSnapshot(id);
      setAnalysis(data);

      const snapshot = snapshots.find(s => s.id === id);
      if (snapshot) {
        setSelectedSnapshot(snapshot);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar snapshot');
    } finally {
      setLoading(false);
    }
  }, [snapshots]);

  const loadSnapshotByName = useCallback(async (datasetName: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await EdaService.getSnapshotByName(datasetName);
      setAnalysis(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar snapshot');
    } finally {
      setLoading(false);
    }
  }, []);

  const selectSnapshot = useCallback((snapshot: any) => {
    setSelectedSnapshot(snapshot);
    loadSnapshot(snapshot.id);

    // Propaga o trace ao AuthContext (persiste em localStorage).
    // Só chama quando o snapshot tem trace_id (evita gravar undefined).
    if (snapshot.trace_id) {
      selectTrace(snapshot.trace_id, snapshot.dataset_name);
    }
  }, [loadSnapshot, selectTrace]);

  useEffect(() => {
    listSnapshots();
  }, [listSnapshots]);

  // Pré-seleciona o snapshot correspondente ao selectedTraceId do AuthContext,
  // quando os snapshots terminarem de carregar. Só casa se o snapshot tiver trace_id.
  useEffect(() => {
    if (selectedTraceId && snapshots.length > 0 && !selectedSnapshot) {
      const match = snapshots.find(s => s.trace_id === selectedTraceId);
      if (match) {
        setSelectedSnapshot(match);
        loadSnapshot(match.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTraceId, snapshots]);

  return {
    snapshots,
    selectedSnapshot,
    analysis,
    loading,
    error,
    listSnapshots,
    loadSnapshot,
    loadSnapshotByName,
    selectSnapshot,
  };
};